import { useCallback } from 'react';

import { useQuery, UseQueryResult } from '@tanstack/react-query';

import { IFilterDefinition } from '@evinced-private/ui-common';

import { DEFAULT_VALUES } from 'src/apps/dashboard/constants/BusinessUnitsTable';
import { useEffectiveFilters } from 'src/apps/dashboard/helpers/FilterHelpers';
import { getBusinessUnitsTableData } from 'src/apps/dashboard/services/business-units-table/BusinessUnitsTableService';
import {
	AggregatedScoreDeltaResults,
	AggregatedScoreWebProducts,
	ScoreWebProduct,
	SummaryIssuesData
} from 'src/apps/dashboard/services/queries/business-units-table/helpers/transformConsolidatedResults';
import { useBusinessUnitsFromConfig } from 'src/apps/dashboard/stores/dashboardConfigStore';
import { IBusinessUnitRow } from 'src/apps/dashboard/types/BusinessUnitsTableTypes';
import { REFETCH_INTERVAL } from 'src/common/consts/QueryConfigs';
import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import { DASHBOARD_BUSINESS_UNITS_TABLE } from 'src/common/providers/reactQueryProvider/QueryKeys';
import { queryClient } from 'src/common/providers/reactQueryProvider/ReactQueryProvider';

interface IUseBusinessUnitsTableDataParams {
	filters?: IFilterOptions;
	filterDefinitions?: IFilterDefinition[];
}

interface IBusinessUnitsTableQueryResult {
	rows: IBusinessUnitRow[];
	total: number;
	unfilteredTotal: number;
	scoreWebProducts: ScoreWebProduct[];
	aggregatedScoreWebProducts: AggregatedScoreWebProducts;
	aggregatedScoreDeltas: AggregatedScoreDeltaResults;
	summaryUniqueIssues: SummaryIssuesData;
	summaryTotalIssues: SummaryIssuesData;
}

export const useBusinessUnitsTableData = ({
	filters,
	filterDefinitions
}: IUseBusinessUnitsTableDataParams): [
	UseQueryResult<IBusinessUnitsTableQueryResult, Error>,
	() => void
] => {
	const { businessUnits, isLoading: isConfigLoading } = useBusinessUnitsFromConfig();
	const effectiveFilters = useEffectiveFilters(filters, filterDefinitions);

	const selectedPeriod = effectiveFilters?.dateRange?.[0]?.value
		? Number(effectiveFilters.dateRange[0].value)
		: DEFAULT_VALUES.SELECTED_PERIOD;

	const queryResult = useQuery<IBusinessUnitsTableQueryResult, Error>(
		[DASHBOARD_BUSINESS_UNITS_TABLE, selectedPeriod, effectiveFilters, businessUnits.length],
		async () =>
			getBusinessUnitsTableData({
				days: selectedPeriod,
				businessUnits,
				filters: effectiveFilters
			}),
		{
			enabled: !isConfigLoading && businessUnits.length > 0,
			keepPreviousData: true,
			refetchInterval: REFETCH_INTERVAL,
			staleTime: REFETCH_INTERVAL
		}
	);

	const invalidate = useCallback(
		() =>
			queryClient.invalidateQueries({
				queryKey: [DASHBOARD_BUSINESS_UNITS_TABLE],
				refetchType: 'none'
			}),
		[]
	);

	return [queryResult, invalidate];
};
